/**
 * Plain names for the provisioning step keys (StepCatalog.cs) — what an admin reads instead of `provision.salesiq`.
 * An unknown key (a step added on the server first) falls back to a readable form of the key, never to nothing.
 */
const APPS: Record<string, string> = {
  iam: 'accounts and sign-in',
  metadata: 'CRM',
  settings: 'settings',
  workflow: 'workflows',
  files: 'files',
  desk: 'help desk',
  vault: 'vault',
  timeline: 'timeline',
  mail: 'email',
  notify: 'notifications',
  telephony: 'telephony',
  reporting: 'reports',
  proofing: 'proofing',
  ai: 'AI',
  agents: 'AI agents',
  salesiq: 'chat (SalesIQ)',
  messaging: 'SMS & WhatsApp',
  billing: 'billing',
};

const INDUSTRY: Record<string, string> = {
  metadata: 'CRM fields and modules',
  ai: 'AI settings',
  skills_pack: 'Skills catalog',
  salesiq: 'Chat menu, replies and tags',
  agents: 'AI agents',
  mail_templates: 'Email templates',
  blueprints: 'Process blueprints',
  approvals: 'Approval rules',
  workflows: 'Workflows',
  views: 'Starter boards',
  records: 'Sample records',
};

/** The removal steps (delete.* / discard.*, 2026-10-02) — the same words for both. */
const REMOVAL: Record<string, string> = {
  approval: 'Another owner approves',
  freeze: 'Freeze the tenant',
  backup: 'Back up and test the backup',
  confirm: 'Confirm by typing the short name',
  snapshot: 'Record the credit balances',
  messaging: 'Close SMS & WhatsApp',
  billing: 'Close billing',
  purge_registry: 'Remove accounts and registry',
  purge_drop: 'Drop the databases',
  verify: 'Check the tenant is gone',
  console: 'Update the console',
};

export function stepName(key: string | null | undefined): string {
  if (!key) return '';
  const removal = /^(delete|discard)\.(.+)$/.exec(key);
  if (removal && REMOVAL[removal[2]!]) return REMOVAL[removal[2]!]!;
  if (key === 'iam.create') return 'Create the tenant';
  if (key === 'gateway.hosts') return 'Web addresses';
  if (key === 'iam.founder_invite') return 'Owner invitation email';
  if (key === 'welcome') return 'Welcome email';
  const [prefix, rest = ''] = key.split(/\.(.*)/s, 2) as [string, string?];
  const app = APPS[rest] ?? rest.replace(/_/g, ' ');
  switch (prefix) {
    case 'provision': return `Set up ${app}`;
    case 'industry': return INDUSTRY[rest] ?? `Industry: ${rest.replace(/_/g, ' ')}`;
    case 'credits': return `Starting ${APPS[rest] ?? rest} credits`;
    case 'verify': return `Check ${app}`;
    default: return key.replace(/[._]/g, ' ');
  }
}

export type StepPhase =
  | 'create' | 'apps' | 'industry' | 'addresses' | 'credits' | 'checks' | 'welcome'
  | 'decide' | 'protect' | 'close' | 'remove' | 'finish' | 'other';

export const PHASE_NAMES: Record<StepPhase, string> = {
  create: 'Create the tenant',
  apps: 'Set up the apps',
  industry: 'Industry content',
  addresses: 'Web addresses',
  credits: 'Starting credits',
  checks: 'Final checks',
  welcome: 'Owner email',
  decide: 'Owner decision',
  protect: 'Freeze and back up',
  close: 'Close accounts',
  remove: 'Remove the tenant',
  finish: 'Final checks',
  other: 'Other steps',
};

export function stepPhase(key: string): StepPhase {
  const removal = /^(delete|discard)\.(.+)$/.exec(key);
  if (removal) {
    switch (removal[2]) {
      case 'approval':
      case 'confirm': return 'decide';
      case 'freeze':
      case 'backup': return 'protect';
      case 'snapshot':
      case 'messaging':
      case 'billing': return 'close';
      case 'purge_registry':
      case 'purge_drop': return 'remove';
      default: return 'finish';
    }
  }
  if (key === 'iam.create') return 'create';
  if (key === 'gateway.hosts') return 'addresses';
  if (key === 'welcome' || key === 'iam.founder_invite') return 'welcome';
  if (key.startsWith('provision.')) return 'apps';
  if (key.startsWith('industry.')) return 'industry';
  if (key.startsWith('credits.')) return 'credits';
  if (key.startsWith('verify.')) return 'checks';
  return 'other';
}

/** Steps grouped by phase, in the order the phases first appear (the catalog order). */
export function groupSteps<T extends { key: string }>(steps: readonly T[]): { phase: StepPhase; steps: T[] }[] {
  const groups: { phase: StepPhase; steps: T[] }[] = [];
  for (const step of steps) {
    const phase = stepPhase(step.key);
    const last = groups.at(-1);
    if (last && last.phase === phase) last.steps.push(step);
    else groups.push({ phase, steps: [step] });
  }
  return groups;
}

export type PhaseState = 'failed' | 'working' | 'gated' | 'waiting' | 'warned' | 'done' | 'cancelled';

/**
 * One word for a phase: the worst of its steps. A phase with ANY cancelled step did not complete — cancelled steps exist
 * only on a cancelled setup — so it never reads "done" because its first steps had finished.
 */
export function phaseState(steps: readonly { status: string; warning?: boolean }[]): PhaseState {
  if (steps.some((s) => s.status === 'failed')) return 'failed';
  // A gated step waits for a PERSON (an approval, a confirmation) or the host backup — not "in progress" on its own.
  if (steps.some((s) => s.status === 'gated')) return 'gated';
  if (steps.some((s) => s.status === 'running' || s.status === 'waiting_retry' || s.status === 'awaiting')) return 'working';
  if (steps.some((s) => s.status === 'pending')) return 'waiting';
  if (steps.some((s) => s.status === 'cancelled')) return 'cancelled';
  if (steps.some((s) => s.warning)) return 'warned';
  return 'done';
}
